import React, { useState, useEffect } from 'react';
import {
  Typography, Tab, Tabs, AppBar, Fab, FormControl, FormLabel, FormGroup,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CreateIcon from '@material-ui/icons/Create';
import CloseIcon from '@material-ui/icons/Close';
import { DispatchOperationTypeName } from '~/utils';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';

import {
  setSelectedLoadOrigin,
  setSelectedTruckOrigin,
  updateEquipmentStatus,
  setCurrentEquipmentStatus,
} from '~/store/dispatch/actions';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MOutlinedInput from '~/components/PF2MOutlinedInput';
import ChatTab from '~/pages/Dispatch/DispatchControl/ChatTab';
import {
  getCodeGroups,
  getCodeTypes,
  getOperatorsGroups,
} from '~/store/manager/actions';
import { getElements, getSubElements } from '~/store/nref/actions';
import { readChat } from '~/store/chat/actions';
import {
  filterCodeGroups,
  filterCodes,
  filterSubElements,
  filterOperators,
  useOnceEffect,
  getNameInListById,
  VISIBLE_CODES,
} from './utils';
import { formatDateTime, parseTimestamp } from '~/utils/moment';


// eslint-disable-next-line no-unused-vars
const TruckModal = React.forwardRef(({ modalData, closeModal, classes }, _ref) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const truck = useSelector((state) => {
    if (typeof modalData === 'number') {
      // pega modalData do redux
      const tr = state.dispatch.truckOrigins.find(t => t.equip_id === modalData);
      if (!tr) return { error: true };
      return tr;
    }
    return modalData;
  });

  const currentEquipmentStatus = useSelector(state => state.dispatch.currentEquipmentStatus);
  const [selectedModalTab, setSelectedModalTab] = useState(0);
  const codeTypes = useSelector(state => state.manager.codeTypes.map(e => ({ ...e, name: translate(`common:${e.name}`) })));
  const codeGroups = useSelector(state => state.manager.codeGroups);
  const codes = useSelector(state => state.manager.codes).filter(
    c => VISIBLE_CODES.includes(c.is_visible),
  );
  const elements = useSelector(state => state.nref.elements);
  const subElements = useSelector(state => state.nref.subElements);
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const operators = useSelector(state => state.manager.operators);
  const operatorGroups = useSelector(state => state.manager.operatorsGroups);
  const chatReadStatus = useSelector(st => (st.chat.chatList?.[modalData.equip_id]?.isReaded));
  const chatStatus = useSelector(
    state => state.manager.platformConfiguration.reduce((acc, e) => (e.key === 'enable_chat' ? e : acc), { value: '0' }),
  );
  const handleClose = () => {
    dispatch(setSelectedLoadOrigin(null));
    dispatch(setSelectedTruckOrigin(null));
    closeModal();
  };

  useOnceEffect(() => {
    // when enter on modal
    dispatch(getCodeTypes());
    dispatch(getCodeGroups());
    dispatch(getSubElements());
    dispatch(getElements());
    dispatch(getOperatorsGroups());
    return true;
  });

  useEffect(() => {
    if (truck.equip_id) {
      dispatch(setCurrentEquipmentStatus(
        equipmentStatuses.find(e => e.id_equip === truck.equip_id),
      ));
    }
  }, [equipmentStatuses, truck.equip_id, dispatch]);

  const renderDisplayField = (label = '', value = '-') => (
    <div>
      <div className={classes.labelTypeSelector}>
        {`${label}:`}
      </div>
      <div style={{ color: '#647886' }}>
        {value}
      </div>
    </div>
  );

  const modalHandleChange = (field, value) => {
    dispatch(setCurrentEquipmentStatus(currentEquipmentStatus, field, value));
  };

  const renderFormControl = (
    item,
    key,
    title,
    collection,
    initialValue = 0,
    collectionId = 'id',
  ) => (
    <FormControl>
      <FormLabel className={classes.formLabel}>
        {title}
      </FormLabel>
      <FormGroup>
        <PF2MSearchSelectOutlined
          placeholder={null}
          className={classnames(classes.field)}
          value={item[key] || 0}
          onChange={e => modalHandleChange(key, e.target.value)}
        >
          {[{ value: initialValue, label: translate('common:Select') }]
            .concat(collection
              .map(e => ({ value: e[collectionId], label: e.name || '0' })))}
        </PF2MSearchSelectOutlined>
      </FormGroup>
    </FormControl>
  );

  const renderOperationsTab = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridColumnGap: 10,
        gridRowGap: 10,
      }}
    >
      {renderDisplayField(
        translate('common:OperatorGroup'),
        getNameInListById(currentEquipmentStatus.id_operator_group, operatorGroups, 'id'),
      )}
      {renderDisplayField(
        translate('common:Operator'), currentEquipmentStatus.operator_name,
      )}
      {renderDisplayField(
        translate('common:CurrentElement'), currentEquipmentStatus.element_name,
      )}
      {renderDisplayField(
        translate('common:CurrentElementPoint'), currentEquipmentStatus.point_name,
      )}
      {renderDisplayField(
        translate('common:CurrentCycleStep'), DispatchOperationTypeName[truck.operation_type],
      )}
      {renderDisplayField(
        translate('common:AllocatedTo'), truck.load_allocation_excavator_name,
      )}
      {renderDisplayField(
        translate('common:Material'), truck.current_material_name,
      )}
      {renderDisplayField(
        translate('common:OriginSubElement'), truck.current_origin_point_name,
      )}
      {renderDisplayField(
        translate('common:DestinationSubElement'), truck.current_destination_point_name,
      )}
      {renderDisplayField(
        translate('common:Velocity'),
        `${truck.velocity ? truck.velocity.toFixed(2) : '0.00'}km/h`,
      )}
    </div>
  );

  const renderStatusEditTab = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridColumnGap: 10,
        gridRowGap: 10,
      }}
    >
      {renderFormControl(currentEquipmentStatus, 'code_type', translate('common:CodeType'), codeTypes, -1)}
      {renderFormControl(currentEquipmentStatus, 'id_code_group', translate('common:CodeGroup'),
        codeGroups.filter(u => filterCodeGroups(u, currentEquipmentStatus)))}
      {renderFormControl(currentEquipmentStatus, 'id_code', translate('common:Code'),
        codes.filter(u => filterCodes(u, currentEquipmentStatus)))}
      {renderFormControl(currentEquipmentStatus, 'element_id', translate('common:Element'), elements)}
      {renderFormControl(currentEquipmentStatus, 'point_id', translate('common:SubElement'),
        subElements.filter(u => filterSubElements(u, currentEquipmentStatus)))}
      {renderFormControl(currentEquipmentStatus, 'id_operator_group', translate('common:OperatorGroup'), operatorGroups)}
      {renderFormControl(currentEquipmentStatus, 'id_operator', translate('common:Operator'),
        operators.filter(u => filterOperators(u, currentEquipmentStatus)), 0, 'id_operator')}
      <div>
        <FormControl style={{
          display: 'block',
        }}
        >
          <FormGroup>
            <FormLabel className={classes.formLabel}>
              {`${translate('common:Comment')}:`}
            </FormLabel>
            <PF2MOutlinedInput
              className={classes.textArea}
              value={currentEquipmentStatus.comment || ''}
              onBlur={e => modalHandleChange('comment', e.target.value)}
              multiline
              rows={4}
            />
          </FormGroup>
        </FormControl>
      </div>
      <Fab
        variant="extended"
        size="medium"
        color="secondary"
        onClick={() => {
          const timestampNow = Math.floor(Date.now() / 1000);
          dispatch(
            updateEquipmentStatus({
              equipmentStatus: {
                ...currentEquipmentStatus,
                start_timestamp: timestampNow,
              },
            }),
          );
        }}
        className={classes.saveButton}
      >
        {translate('common:SaveData')}
      </Fab>
    </div>
  );

  const renderStatusTab = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridColumnGap: 10,
        gridRowGap: 10,
      }}
    >
      {renderDisplayField(
        translate('common:CodeType'),
        getNameInListById(currentEquipmentStatus.code_type, codeTypes, 'id'),
      )}
      {renderDisplayField(
        translate('common:CodeGroup'),
        getNameInListById(
          currentEquipmentStatus.id_code_group,
          codeGroups.filter(u => filterCodeGroups(u, currentEquipmentStatus)),
          'id',
        ),
      )}
      {renderDisplayField(
        translate('common:Code'),
        // O id do código se repete entre grupos (ex.: Omissão × Serv. Externo).
        getNameInListById(
          currentEquipmentStatus.id_code,
          codes.filter(c => filterCodes(c, currentEquipmentStatus)),
          'id',
        ),
      )}
      {renderDisplayField(
        translate('common:CurrentElement'),
        getNameInListById(currentEquipmentStatus.element_id, elements, 'id'),
      )}
      {renderDisplayField(
        translate('common:CurrentElementPoint'),
        getNameInListById(currentEquipmentStatus.point_id, subElements, 'id'),
      )}
      {renderDisplayField(
        translate('common:OperatorGroup'),
        getNameInListById(currentEquipmentStatus.id_operator_group, operatorGroups, 'id'),
      )}
      {renderDisplayField(
        translate('common:Operator'),
        getNameInListById(currentEquipmentStatus.id_operator, operators, 'id_operator'),
      )}
    </div>
  );

  const renderHeader = () => (
    <div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
        >
          {`${translate('common:Equipment')}: ${truck.equip_name}`}
        </Typography>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
        >
          {`${translate('common:Fleet')}: ${truck.equip_group_name}`}
        </Typography>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridColumnGap: 10,
        gridRowGap: 10,
      }}
      >
        {renderDisplayField(translate('common:LastServerConnection'),
          truck.timestamp_sync_request
            ? formatDateTime(parseTimestamp(truck.timestamp_sync_request / 1)) : 'N/A')}
        {renderDisplayField(translate('common:LastDataSent'),
          truck.timestamp_last_data_sent
            ? formatDateTime(parseTimestamp(truck.timestamp_last_data_sent / 1)) : 'N/A')}
        {renderDisplayField(translate('common:DiconnectedTime'), truck.tempo_diff ? `${truck.tempo_diff } min` : 'N/A')}
        {renderDisplayField(translate('common:DataToSend'), truck.remaining_data_in_package) || 'N/A'}
      </div>
    </div>
  );

  const renderTabBar = () => (
    <AppBar
      position="static"
      style={{
        backgroundColor: 'white',
        boxShadow: 'none',
      }}
    >
      <div>
        <Tabs
          value={selectedModalTab}
          onChange={(event, value) => {
            setSelectedModalTab(value);
            dispatch(readChat(modalData.equip_id));
          }}
          classes={{ root: classes.tabsRoot, indicator: classes.tabsIndicator }}
        >
          <Tab
            label={'Status'}
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
          <Tab
            label={'Edit Status'}
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
          {chatStatus.value === '1' && (
            <Tab
              label={translate('common:ChatBeta')}
              classes={{
                root: chatReadStatus ? classes.tabRoot : classes.chatTabWithMessage,
                selected: classes.tabSelected,
              }}
            />
          )}
          <Tab
            label={'Operação'}
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
        </Tabs>
      </div>
    </AppBar>
  );

  return (
    <div style={{ width: 800, height: 600 }}>
      <Card style={{ height: '100%' }}>
        <CardHeader icon>
          <>
            <CardIcon color="warning">
              <CreateIcon />
            </CardIcon>
            <CardIcon
              onClick={handleClose}
              style={{
                float: 'right',
                cursor: 'pointer',
                background: 'darkgray',
                borderRadius: 50,
                boxShadow: '10px 10px 50px gray',
              }}
            >
              <CloseIcon />
            </CardIcon>
          </>
        </CardHeader>
        <CardBody>
          <div />
          {truck.error ? (
            <div>
              <h2>{translate('common:FailToLoadData')}</h2>
            </div>
          ) : (
            <div>
              {renderHeader()}
              <hr />
              <div>
                {renderTabBar()}
                <div style={{ padding: 20 }}>
                  {selectedModalTab === 0 && renderStatusTab()}
                  {selectedModalTab === 1 && renderStatusEditTab()}
                  {chatStatus.value === '1' && selectedModalTab === 2 && <ChatTab selectedChatId={modalData.equip_id} />}
                  {(selectedModalTab === 3 || (chatStatus.value === '0' && selectedModalTab === 2)) && renderOperationsTab()}
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
});

TruckModal.propTypes = {
  classes: PropTypes.shape({
    labelTypeSelector: PropTypes.string.isRequired,
    formLabel: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    textArea: PropTypes.string,
    saveButton: PropTypes.string.isRequired,
    tabsRoot: PropTypes.string,
    tabSelected: PropTypes.string.isRequired,
    tabRoot: PropTypes.string.isRequired,
    tabsIndicator: PropTypes.string.isRequired,
    chatTabWithMessage: PropTypes.string.isRequired,
  }).isRequired,
  closeModal: PropTypes.func,
  modalData: PropTypes.oneOfType([
    // it's possible to pass two values
    PropTypes.number, // truckId for fetch the info
    PropTypes.shape({ // the necessaryInfo for the modal
      equip_id: PropTypes.number.isRequired,
      equip_name: PropTypes.string.isRequired,
      equip_group_name: PropTypes.string.isRequired,
      equip_type_name: PropTypes.string.isRequired,
      operator_group_name: PropTypes.string.isRequired,
      operator_name: PropTypes.string.isRequired,
      current_element_name: PropTypes.string,
      current_element_point_name: PropTypes.string.isRequired,
      load_allocation_excavator_name: PropTypes.string.isRequired,
      current_material_name: PropTypes.string.isRequired,
      current_origin_point_name: PropTypes.string.isRequired,
      current_destination_point_name: PropTypes.string.isRequired,
      operation_type: PropTypes.number.isRequired,
      velocity: PropTypes.number.isRequired,
      code_type_id: PropTypes.number.isRequired,
      code_group_name: PropTypes.string.isRequired,
      code_name: PropTypes.string.isRequired,
      last_modified: PropTypes.string.isRequired,
    }),
  ]),
};

TruckModal.defaultProps = {
  closeModal: () => null,
  modalData: null,
};

export default TruckModal;
